import { createFileRoute, Link } from "@tanstack/react-router";

import { NewMapsCountdown } from "#/components/NewMapsCountdown";
import { PageEyeBrow } from "#/components/PageEyeBrow";
import { PageHeader } from "#/components/PageHeader";
import { PageIntro } from "#/components/PageIntro";
import { SectionHeader } from "#/components/SectionHeader";
import { Button } from "#/components/ui/button";
import { Card, CardDescription, CardFooter, CardHeader, CardTitle } from "#/components/ui/card";
import { UpdatesBanner } from "#/components/UpdatesBanner";
import { DOCUMENT_TITLES, documentHead } from "#/lib/documentTitle";
import { globalStatsQueryOptions } from "#/lib/queries";
import { loadPlayIndexData } from "#/server/game/playIndex";

import { EarlyBirdPromoCard } from "./-index.earlyBirdPromo";
import { GlobalStatsSection } from "./-index.globalStats";
import { LearnFromTheBestSection } from "./-index.learnFromTheBest";
import { MonthlyHiscoresMarquee } from "./-index.monthlyHiscoresMarquee";
import { DailyMapColumn } from "./-play.heroAndLeaderboards";

export const Route = createFileRoute("/")({
  head: () => documentHead(DOCUMENT_TITLES.home),
  component: HomePage,
  loader: async ({ context }) => {
    const data = await loadPlayIndexData();
    context.queryClient.setQueryData(globalStatsQueryOptions().queryKey, data.globalStats);
    return data;
  },
});

function HomePage() {
  const data = Route.useLoaderData();

  return (
    <>
      {data.latestUpdate && !data.isAnonymous ? (
        <UpdatesBanner
          latestUpdate={data.latestUpdate}
          serverLastSeenUpdateId={data.lastSeenUpdateId}
        />
      ) : null}
      <main className="mx-auto w-full max-w-[1080px] px-4 py-10">
        <div className="mx-auto flex w-full max-w-3xl flex-col gap-6">
          <PageIntro>
            <PageEyeBrow>
              <NewMapsCountdown />
            </PageEyeBrow>
            <PageHeader>{data.todayStr}</PageHeader>
          </PageIntro>
          {data.showEarlyBirdPromo ? <EarlyBirdPromoCard /> : null}
          <MonthlyHiscoresMarquee entries={data.monthlyHiscoreLeaders} monthKey={data.monthKey} />
          <div className="grid w-full grid-cols-1 gap-6 sm:grid-cols-2">
            <DailyMapColumn
              title="Standard"
              mapId={data.standardMapId}
              bestDays={data.standardBestDays}
              leaderboardEntries={data.standardLeaderboard}
              medalThresholds={data.standardMedalThresholds}
              access={data.accessByMapId[data.standardMapId] ?? { ok: true }}
              dailyStreak={data.standardDailyStreak}
            />
            <DailyMapColumn
              title="Premium"
              mapId={data.premiumMapId}
              bestDays={data.premiumBestDays}
              leaderboardEntries={data.premiumLeaderboard}
              medalThresholds={data.premiumMedalThresholds}
              access={data.accessByMapId[data.premiumMapId] ?? { ok: true }}
              dailyStreak={data.premiumDailyStreak}
            />
          </div>
          <GlobalStatsSection linkToStats />
          {data.yesterdayBoards ? <LearnFromTheBestSection boards={data.yesterdayBoards} /> : null}
          <TutorialPromptCard />
          <ArchivePromptCard />
        </div>
      </main>
    </>
  );
}

function TutorialPromptCard() {
  return (
    <Card className="mx-auto w-full max-w-sm">
      <CardHeader>
        <CardTitle>
          <SectionHeader>New to Daily Wars?</SectionHeader>
        </CardTitle>
        <CardDescription>
          Check out the tutorial to get to know the basics, or check out the{" "}
          <Link
            to="/codex"
            preload={false}
            className="font-medium text-primary underline underline-offset-4 hover:text-foreground"
          >
            guide
          </Link>{" "}
          for more details.
        </CardDescription>
      </CardHeader>
      <CardFooter>
        <Button
          variant="default"
          size="lg"
          className="mx-auto"
          render={<Link to="/tutorial" preload={false} />}
          nativeButton={false}
        >
          Play tutorial →
        </Button>
      </CardFooter>
    </Card>
  );
}

function ArchivePromptCard() {
  return (
    <Card className="mx-auto w-full max-w-sm">
      <CardHeader>
        <CardTitle>
          <SectionHeader>Missed a day?</SectionHeader>
        </CardTitle>
        <CardDescription>Replay past daily maps from the archive.</CardDescription>
      </CardHeader>
      <CardFooter>
        <Button
          size="lg"
          className="mx-auto"
          render={<Link to="/archive" preload={false} />}
          nativeButton={false}
        >
          View archive →
        </Button>
      </CardFooter>
    </Card>
  );
}
